import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Drawer,
  IconButton,
} from "@mui/material";
import { FaBars, FaShoppingCart } from "react-icons/fa";
import { styled, Theme, CSSObject } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";

import { useAppSelector, useAppDispatch } from "features/config/hooks";
import {
  getAdminSession,
  selectGetAdminSession,
} from "features/admin/presentation/slices/get-admin-session.slice";
import {
  selectstockOrderGlobalTrigger,
  togglestockOrderSideBar,
} from "../slices/stock-order.slice";
import { BsArrowReturnLeft } from "react-icons/bs";
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useEffect, useState } from "react";
import {
  selectLogoutAdmin,
  LogoutAdminState,
  resetLogoutAdmin,
  logoutAdmin,
} from "features/admin/presentation/slices/logout-admin.slice";
import React from "react";
import { HiOutlineClipboardCheck } from "react-icons/hi";
import { IoSettingsSharp } from "react-icons/io5";
import {
  MdOutlinePostAdd,
  MdOutlineExpandMore,
  MdInventory,
} from "react-icons/md";
import { TbLogout } from "react-icons/tb";
import { RiDashboardLine, RiHomeSmileLine } from "react-icons/ri";
import { BiUser } from "react-icons/bi";

interface MenuAvailability {
  dashboard: boolean;
  stockOrderingOrders: boolean;
  selling: boolean;
  teiHubLink: boolean;
  inventoryTransferStocks: boolean;
  settings: boolean;
  procurement: boolean;
  task: boolean;
  profile: boolean;
  returnToLanding: boolean;
}

export function StockSidebarWrapper() {
  const location = useLocation();

  useEffect(() => {
    document.getElementById("audit-main-section")?.scroll(0, 0);
  }, [location]);

  return (
    <main className="flex min-h-screen">
      <StockDrawer />
      <section className="flex-1 h-screen overflow-y-auto bg-paper">
        <Outlet />
      </section>
    </main>
  );
}

/**
 * ============
 * DRAWER
 * ====
 * @returns
 */

const drawerWidth = "17rem";

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `5rem`,
});

const Drawers = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  ...theme.mixins.toolbar,
}));

function StockDrawer() {
  const stockOrderSideBar = useAppSelector(selectstockOrderGlobalTrigger);

  const dispatch = useAppDispatch();

  return (
    <>
      <div className="md:hidden">
        <Drawer
          className="md:hidden"
          anchor={"left"}
          open={stockOrderSideBar.sidebarStatus}
          onClose={() => {
            dispatch(togglestockOrderSideBar());
          }}
        >
          <SideBarContent />
        </Drawer>
      </div>

      <div className="hidden md:flex">
        <Drawers
          anchor="left"
          variant="permanent"
          sx={{ zIndex: 10 }}
          open={stockOrderSideBar.sidebarStatus}
          onClose={() => {
            dispatch(togglestockOrderSideBar());
          }}
        >
          <SideBarContent />
        </Drawers>
      </div>
    </>
  );
}

function SideBarContent() {
  const stockOrderSideBar = useAppSelector(selectstockOrderGlobalTrigger);
  const getAdminSessionState = useAppSelector(selectGetAdminSession);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return (
    <Box className="relative h-screen bg-gradient-to-b from-slate-800 to-slate-900 font-['Varela_Round'] duration-500 z-10 shadow-2xl flex flex-col">
      {/* Fixed Header Section */}
      <div className="flex-shrink-0 bg-gradient-to-r from-slate-800 to-slate-700 border-b border-slate-600">
        <DrawerHeader className="!min-h-[0px] px-4 py-6">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <img
                  src={require("assets/favicon.png")}
                  className="w-10 h-10 bg-white border-2 rounded-lg shadow-lg border-red-500 transition-all duration-300"
                  alt="Material System Logo"
                />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <div
                className={`transition-all duration-300 ${
                  !stockOrderSideBar.sidebarStatus
                    ? "opacity-0 w-0 overflow-hidden"
                    : "opacity-100"
                }`}
              >
                <h1 className="text-lg font-bold text-white">
                  Material System
                </h1>
                <p className="text-xs text-slate-300">Admin Dashboard</p>
              </div>
            </div>
            <button
              onClick={() => {
                dispatch(togglestockOrderSideBar());
              }}
              className={`p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-600 transition-all duration-200 ${
                !stockOrderSideBar.sidebarStatus ? "ml-0" : "ml-auto"
              }`}
            >
              <FaBars size={16} />
            </button>
          </div>
        </DrawerHeader>
      </div>

      {/* Scrollable Content Container */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {/* Return to Landing Section */}
          {(getAdminSessionState.data?.admin.user_details.hubLink_groups
            .length === 0 ||
            getAdminSessionState.data?.admin.user_details.id === 1) && (
            <div className="px-4 py-4 border-b border-slate-600">
              <button
                onClick={() => {
                  navigate("/admin/landing");
                }}
                className={`w-full group relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all duration-300 ${
                  !stockOrderSideBar.sidebarStatus ? "p-3" : "p-4"
                }`}
              >
                <div className="absolute inset-0 bg-white opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>
                <div
                  className={`relative flex items-center ${
                    !stockOrderSideBar.sidebarStatus
                      ? "justify-center"
                      : "space-x-3"
                  }`}
                >
                  <div className="flex items-center justify-center w-8 h-8 bg-white bg-opacity-20 rounded-lg">
                    <RiHomeSmileLine size={18} className="text-white" />
                  </div>
                  <div
                    className={`text-left transition-all duration-300 ${
                      !stockOrderSideBar.sidebarStatus
                        ? "opacity-0 w-0 overflow-hidden"
                        : "opacity-100"
                    }`}
                  >
                    <div className="text-sm font-semibold text-white">
                      Return to Home
                    </div>
                    <div className="text-xs text-blue-100">Main menu page</div>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* User Info Section */}
          <div className="px-4 py-4 border-b border-slate-600">
            <div
              className={`transition-all duration-300 ${
                !stockOrderSideBar.sidebarStatus
                  ? "opacity-0 h-0 overflow-hidden"
                  : "opacity-100"
              }`}
            >
              <div className="bg-slate-700 bg-opacity-50 rounded-xl p-4 border border-slate-600">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center">
                    <BiUser size={20} className="text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">
                      {getAdminSessionState.data?.admin.user_details
                        .first_name +
                        " " +
                        getAdminSessionState.data?.admin.user_details.last_name}
                    </div>
                    {getAdminSessionState.data?.admin.user_details.company !==
                      null && (
                      <div className="text-xs text-slate-300">
                        {
                          getAdminSessionState.data?.admin.user_details.company
                            .name
                        }
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
          <div className="px-4 py-4 pb-6">
            <StockOrderDrawerMenu />
          </div>
        </div>

        {/* Fixed Footer with Logout - Always at Bottom */}
        <div className="flex-shrink-0 bg-gradient-to-r from-slate-800 to-slate-700 border-t border-slate-600 px-4 py-4">
          <LogoutButton />
        </div>
      </div>
    </Box>
  );
}

/**
 * =========
 * LOGOUT BUTTON COMPONENT
 * =====
 */
function LogoutButton() {
  const stockOrderSideBarState = useAppSelector(selectstockOrderGlobalTrigger);
  const dispatch = useAppDispatch();

  return (
    <button
      onClick={() => {
        dispatch(logoutAdmin());
      }}
      className={`w-full group relative overflow-hidden rounded-xl bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 transition-all duration-300 ${
        !stockOrderSideBarState.sidebarStatus ? "p-3" : "p-4"
      }`}
    >
      <div className="absolute inset-0 bg-white opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>
      <div
        className={`relative flex items-center ${
          !stockOrderSideBarState.sidebarStatus ? "justify-center" : "space-x-3"
        }`}
      >
        <div className="flex items-center justify-center w-8 h-8 bg-white bg-opacity-20 rounded-lg">
          <TbLogout size={18} className="text-white" />
        </div>
        <div
          className={`text-left transition-all duration-300 ${
            !stockOrderSideBarState.sidebarStatus
              ? "opacity-0 w-0 overflow-hidden"
              : "opacity-100"
          }`}
        >
          <div className="text-sm font-semibold text-white">Sign Out</div>
          <div className="text-xs text-red-100">End your session</div>
        </div>
      </div>
    </button>
  );
}

/**
 * =========
 * MENUS
 * =====
 */

function StockOrderDrawerMenu() {
  const { view } = useParams();

  const stockOrderSideBarState = useAppSelector(selectstockOrderGlobalTrigger);
  const getLogoutAdminState = useAppSelector(selectLogoutAdmin);
  const getAdminSessionState = useAppSelector(selectGetAdminSession);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [menuAvailability, setMenuAvailability] = useState<MenuAvailability>({
    dashboard: false,
    stockOrderingOrders: false,
    selling: false,
    teiHubLink: false,
    inventoryTransferStocks: false,
    settings: false,
    procurement: false,
    task: false,
    profile: false,
    returnToLanding: false,
  });
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  const noStockOrderingPermission =
    getAdminSessionState.data?.admin.user_details.sos_groups.some(
      (group) =>
        group.id === 1 ||
        group.id === 2 ||
        group.id === 3 ||
        group.id === 4 ||
        group.id === 5 ||
        group.id === 6
    );

  useEffect(() => {
    const { user_details } = getAdminSessionState.data?.admin ?? {};
    const enableHubLink =
      getAdminSessionState.data?.admin.user_details.hubLink_groups.length !== 0;
    const enableStockOrdering =
      getAdminSessionState.data?.admin.user_details.sos_groups.length !== 0 &&
      Boolean(noStockOrderingPermission);

    setMenuAvailability({
      ...menuAvailability,
      dashboard: false, // in development set to false
      stockOrderingOrders: view === "order" && enableStockOrdering,
      selling: view === "selling",
      teiHubLink: enableHubLink, // moved to another route
      inventoryTransferStocks: false, //For Inventory Development: set true
      settings: false, // refactor settings and temporary set to false for selling  view === "selling"
      procurement:
        user_details?.sos_groups.some((grp) => grp.id === 2) ?? false,
      task: false, // in development set to false
      profile: false, // in development set to false
      returnToLanding: enableStockOrdering,
    });
  }, [
    getAdminSessionState.data?.admin.user_details,
    getAdminSessionState.data,
  ]);

  const menuitems = [
    {
      text: "Order",
      path: "order",
      icon: <FaShoppingCart size={18} />,
      enable: view === "order" || view === "procurement",
    },
    {
      text: "Selling",
      path: "selling",
      icon: <MdOutlinePostAdd size={18} />,
      enable: view === "selling",
    },
    {
      text: "Products Management",
      path: "selling/products",
      icon: <MdInventory size={18} />,
      enable: view === "selling",
    },
    {
      text: "Procurement",
      path: "procurement",
      icon: <HiOutlineClipboardCheck size={18} />,
      enable: menuAvailability.procurement,
    },
  ];

  useEffect(() => {
    if (getLogoutAdminState.status === LogoutAdminState.success) {
      dispatch(getAdminSession());
      dispatch(resetLogoutAdmin());
      navigate("/admin");
    }
  }, [getLogoutAdminState, dispatch, navigate]);

  const settingsMenuItems = [
    {
      text: "General Settings",
      path: "selling/settings/general-settings",
      icon: <IoSettingsSharp size={16} />,
      enable: view === "selling",
    },
  ];

  return (
    <div className="space-y-2">
      <nav>
        <ul className="space-y-1">
          {/* Main Menu Items */}
          {menuitems.map((item, index) => {
            const { text, path, icon, enable } = item;
            const key = index;
            return (
              <React.Fragment key={key}>
                {enable && (
                  <li>
                    <NavLink
                      to={path}
                      end
                      className={(navData) =>
                        navData.isActive
                          ? `flex items-center rounded-lg bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg transition-all duration-200 ${
                              !stockOrderSideBarState.sidebarStatus
                                ? "p-3 justify-center"
                                : "p-3"
                            }`
                          : `flex items-center rounded-lg text-slate-300 hover:bg-slate-700 hover:text-white transition-all duration-200 group ${
                              !stockOrderSideBarState.sidebarStatus
                                ? "p-3 justify-center"
                                : "p-3"
                            }`
                      }
                    >
                      <div
                        className={`flex items-center ${
                          !stockOrderSideBarState.sidebarStatus
                            ? ""
                            : "space-x-3"
                        }`}
                      >
                        <span className="flex-shrink-0 group-hover:text-red-400 transition-colors duration-200">
                          {icon}
                        </span>
                        <span
                          className={`font-medium transition-all duration-300 ${
                            !stockOrderSideBarState.sidebarStatus
                              ? "opacity-0 w-0 overflow-hidden ml-0"
                              : "opacity-100 ml-3"
                          }`}
                        >
                          {text}
                        </span>
                      </div>
                    </NavLink>
                  </li>
                )}
              </React.Fragment>
            );
          })}

          {/* Settings Accordion */}
          {view === "selling" && menuAvailability.settings && (
            <li className="mt-2">
              <Accordion
                onChange={(event, isExpanded) => {
                  setSettingsExpanded(isExpanded);
                }}
                sx={{
                  color: "white",
                  borderRadius: "8px",
                  backgroundColor: "rgba(51, 65, 85, 0.6)",
                  boxShadow: "none",
                  border: "1px solid rgba(71, 85, 105, 0.5)",
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<MdOutlineExpandMore color="white" size={20} />}
                  sx={{
                    paddingX: stockOrderSideBarState.sidebarStatus
                      ? "12px"
                      : "12px",
                    minHeight: "48px",
                    "&.Mui-expanded": {
                      minHeight: "48px",
                    },
                  }}
                >
                  <div
                    className={`flex items-center ${
                      !stockOrderSideBarState.sidebarStatus ? "" : "space-x-3"
                    }`}
                  >
                    <span className="flex-shrink-0">
                      <IoSettingsSharp color="white" size={18} />
                    </span>
                    <span
                      className={`font-medium transition-all duration-300 ${
                        !stockOrderSideBarState.sidebarStatus
                          ? "opacity-0 w-0 overflow-hidden ml-0"
                          : "opacity-100 ml-3"
                      }`}
                    >
                      Settings
                    </span>
                  </div>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <div className="space-y-1 p-2">
                    {settingsMenuItems.map((item, index) => {
                      const { text, path, icon, enable } = item;
                      const key = index;
                      return (
                        <React.Fragment key={key}>
                          {enable && (
                            <NavLink
                              to={path}
                              className={(navData) =>
                                navData.isActive
                                  ? `flex items-center rounded-lg bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg transition-all duration-200 ${
                                      !stockOrderSideBarState.sidebarStatus
                                        ? "p-2 justify-center"
                                        : "p-2"
                                    }`
                                  : `flex items-center rounded-lg text-slate-300 hover:bg-slate-600 hover:text-white transition-all duration-200 group ${
                                      !stockOrderSideBarState.sidebarStatus
                                        ? "p-2 justify-center"
                                        : "p-2"
                                    }`
                              }
                            >
                              <div
                                className={`flex items-center ${
                                  !stockOrderSideBarState.sidebarStatus
                                    ? ""
                                    : "space-x-3"
                                }`}
                              >
                                <span className="flex-shrink-0 group-hover:text-red-400 transition-colors duration-200">
                                  {icon}
                                </span>
                                <span
                                  className={`text-sm font-medium transition-all duration-300 ${
                                    !stockOrderSideBarState.sidebarStatus
                                      ? "opacity-0 w-0 overflow-hidden ml-0"
                                      : "opacity-100 ml-3"
                                  }`}
                                >
                                  {text}
                                </span>
                              </div>
                            </NavLink>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </AccordionDetails>
              </Accordion>
            </li>
          )}
        </ul>
      </nav>
    </div>
  );
}
